import { computed } from "vue";
import { useForm } from "@inertiajs/vue3";

// Thin wrapper over Inertia's useForm. It standardizes only the repeated bits:
// the verb + url, the preserveScroll default, the processing flag, and an
// optional reset-on-success. It deliberately does NOT own user-facing
// messaging — success/error text already flows globally through server
// flash -> HandlePropsNotifications -> notify -> toast. The raw Inertia `form`
// is returned untouched, so every escape hatch (transform, forceFormData,
// errorBag, form.errors + FormError, v-model) keeps working unchanged.
//
// Single-`form.<verb>` submits only. Multi-form / axios / router flows stay
// hand-rolled (see the design doc's skip set).
export function useSubmittableForm(fields, config = {}) {
    const {
        url,
        method = "post",
        preserveScroll = true,
        resetOnSuccess = false,
        onSuccess,
        options = {},
    } = config;

    const form = useForm(fields);

    const applyReset = () => {
        if (resetOnSuccess === false) return;
        if (Array.isArray(resetOnSuccess)) {
            form.reset(...resetOnSuccess);
        } else if (resetOnSuccess === true) {
            form.reset();
        }
    };

    // overrides.url / .method / .options enable create-or-update without a
    // bespoke submit. The wrapped onSuccess runs the reset before the caller's
    // callback and is applied LAST so it always wins over any onSuccess that
    // leaked in through options/overrides.
    const submit = (overrides = {}) => {
        const verb = overrides.method ?? method;
        const resolvedUrl = overrides.url ?? (typeof url === "function" ? url(form) : url);

        const wrapped = (page) => {
            applyReset();
            onSuccess?.(page);
        };

        form[verb](resolvedUrl, {
            preserveScroll,
            ...options,
            ...overrides.options,
            onSuccess: wrapped,
        });
    };

    const processing = computed(() => form.processing);

    return { form, submit, processing };
}
